import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import BoilerplateForm from './boilerplate_form';
import { html, nothing } from 'lit';
import formatMoney from '../utilities/formatMoney';

export class GiveawayAdditionalForm extends BoilerplateForm {
  static get properties() {
    return {
      currentOrder: {
        type: Object,
        attribute: 'current-order',
        converter: (value) => JSON.parse(value),
      },
      customerId: {
        type: String,
        attribute: 'customer-id',
      },
      giveawayHandle: {
        type: String,
        attribute: 'giveaway-handle',
        reflect: false,
      },
      entriesCount: {
        type: Number,
        attribute: 'entries-count',
      },
      shopHref: {
        type: String,
        attribute: 'shop-href',
      },
      shopLabel: {
        type: String,
        attribute: 'shop-label',
      },
      shopAriaLabel: {
        type: String,
        attribute: 'shop-aria-label',
      },
    };
  }

  constructor() {
    super();

    document.addEventListener('order-selected', () => {
      this.updateFormState('initial');
    });
  }

  async handleSubmit(event) {
    event.preventDefault();

    const body = {
      ...this.objectData,
      config: this.region,
      giveawayHandle: this.giveawayHandle,
    };

    try {
      const response = await fetch(this.action, {
        method: this.method,
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          'x-wf-token': this.token,
          'x-wf-value': this.value,
        },
      });

      const data = await response.json();

      if (!data.success) {
        this.handleErrors(
          data.errors || data.error || 'An unknown error occurred'
        );
        return;
      }

      const snakeCaseHandle = this.giveawayHandle.replaceAll('-', '_');

      window.dispatchEvent(new CustomEvent('analytics:gtag', {
        detail: {
          event: `additional_entry_${snakeCaseHandle}`,
          params: { region: this.region },
        }
      }));

      this.handleSuccess();
    } catch (error) {
      this.handleErrors(error);
    }
  }

  handleSuccess() {
    this.errors = {};
    this.genericErrorSegments = [];
    this.updateFormState('success', true);
  }

  renderFormElements() {
    return html`
      <div class="giveaway_form_extra_chance_select_container">
        <p class="giveaway_form_reason_label">
          ${window.theme.strings.giveaway.reason_label}
        </p>
        <input
          type="hidden"
          name="orderId"
          value="${this.currentOrder?.order_id}"
        />
        <input type="hidden" name="customerId" value="${this.customerId}" />
        <float-textarea
          name="submission"
          id="submission"
          required
          error="Please enter your reason"
          placeholder="Reason"
          max-words="25"
          maxLength="500"
          single-line
        ></float-textarea>
      </div>
    `;
  }

  renderSuccess() {
    const strings = window.theme.strings.giveaway;
    const count = this.entriesCount ?? 1;
    const entriesLine =
      count === 1
        ? strings.success_entries_line_one
        : strings.success_entries_line_other.replace(
            '{{ entries_count }}',
            String(count)
          );
    const href = this.shopHref || window.Shopify.routes.root;
    const label = (this.shopLabel && this.shopLabel.trim()) || strings.success_no_extra_chance_button;
    const aria = (this.shopAriaLabel && this.shopAriaLabel.trim()) || label;

    return html`
      <div class="giveaway_form_extra_chance_success">
        ${this.currentOrder
          ? html`
            <ul class="giveaway_form_success_orders_list">
              <li class="giveaway_form_success_order_row _entered">
                <span class="giveaway_form_success_order_number">
                  ${this.currentOrder.order_number}
                </span>
                <span class="giveaway_form_success_order_price">
                  ${formatMoney(this.currentOrder.order_total)}
                </span>
              </li>
            </ul>`
          : nothing}
        <div class="giveaway_form_extra_chance_success_title_container">
          <h3 class="giveaway_form_extra_chance_success_title">
            ${strings.extra_chance_success_title}
          </h3>
          <h4 class="giveaway_form_extra_chance_success_subtitle">
            ${strings.extra_chance_success_subtitle}
          </h4>
        </div>
        <div class="giveaway_form_extra_chance_success_content">
          <p class="giveaway_form_success_entries_line">${entriesLine}</p>
          ${unsafeHTML(strings.success_more_entries_html)}
        </div>
        <a
          href="${href}"
          class="btn btn-primary"
          aria-label="${aria}"
        >
          ${label}
        </a>
      </div>
    `;
  }

  renderButton() {
    return html`
      <button
        type="submit"
        class="btn btn-primary"
        aria-label="Submit extra chance giveaway form"
        ?disabled=${this.loading}
        aria-busy=${this.loading ? 'true' : 'false'}
      >
        ${this.loading
          ? html`<bouncing-ellipsis></bouncing-ellipsis>`
          : 'Submit'}
      </button>
    `;
  }
}

window.customElements.get('giveaway-additional-form') ||
  window.customElements.define(
    'giveaway-additional-form',
    GiveawayAdditionalForm
  );
